import { useState, useEffect, useMemo } from "react";
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  Button,
  Modal,
  Spin,
  DatePicker,
} from "antd";
import dayjs from "dayjs";
import {
  LoadingOutlined,
  BankOutlined,
  SafetyCertificateOutlined,
  RightOutlined,
  DownOutlined,
  IdcardOutlined,
  EnvironmentOutlined,
  UserOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  parseFieldsToState,
  parseStateToFields,
} from "../../../utils/parseFormfields";
import { useDispatch } from "react-redux";
import { setEntity, setPartyAt } from "../../../features/cartransferSlice";
import UploadFile from "../../../utils/uploadFile";
import {
  regions,
  findCommunes,
  findRegion,
  correctCommune,
} from "../../../assets/geo";
import { validate, format } from "rut.js";
import { fetchRutificador } from "../../../api/parseDocsService";
import { toast } from "../../../utils/toasts";
import { handleError } from "../../../utils/errorHandler";
import ImageInstructionsModal from "../components/ImageInstructionsModal";
import ExpiredModal from "../components/ExpiredModal";
import { Checkbox } from "antd";
import { notarias } from "../../../utils/notarias";
import RutInput from "../../../components/RutInput";
import "./PersonForm.css";

const { Option } = Select;

// ── Visual helpers ───────────────────────────────────────────────────
const normalize = (value = "") =>
  typeof value === "string"
    ? value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
    : "";

const safeText = (text) => {
  if (!text) return "";
  if (typeof text !== "string") return String(text);
  return text.normalize("NFC");
};

const FieldSection = ({ icon, label }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 1.2,
      textTransform: "uppercase",
      color: "#b0b0b0",
      marginBottom: 10,
      marginTop: 4,
      paddingBottom: 6,
      borderBottom: "1px solid #f0f0f0",
    }}
  >
    {icon && <span style={{ fontSize: 11 }}>{icon}</span>}
    {label}
  </div>
);

const SectionBlock = ({ children, active, color }) => (
  <div
    style={{
      background: active ? color + "08" : "#fafafa",
      border: `1px solid ${active ? color + "30" : "#efefef"}`,
      borderRadius: active ? "0 0 8px 8px" : 8,
      borderTop: "none",
      padding: "14px 14px 4px",
      marginBottom: 10,
    }}
  >
    {children}
  </div>
);

const SectionToggle = ({ checked, onToggle, icon, title, color }) => (
  <div
    onClick={onToggle}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "9px 14px",
      background: checked ? color + "10" : "#fafafa",
      border: `1px solid ${checked ? color + "40" : "#e8e8e8"}`,
      borderRadius: checked ? "8px 8px 0 0" : 8,
      cursor: "pointer",
      userSelect: "none",
      marginBottom: checked ? 0 : 8,
      transition: "background 0.15s, border-color 0.15s",
    }}
  >
    <span style={{ color: checked ? color : "#b0b0b0", fontSize: 15 }}>{icon}</span>
    <span style={{ flex: 1, fontWeight: 600, fontSize: 13, color: checked ? color : "#666" }}>
      {title}
    </span>
    <span style={{ color: checked ? color : "#d0d0d0", fontSize: 10, transition: "transform 0.2s" }}>
      {checked ? <DownOutlined /> : <RightOutlined />}
    </span>
  </div>
);

const fi = { marginBottom: 10 }; // form item default margin

// ── Component ────────────────────────────────────────────────────────
const PersonForm = ({ role, partyRole, partyIndex, person, label, cartransferId, accentColor = "#1d3557" }) => {
  const [communes, setCommunes] = useState(findCommunes("Región de los Lagos") || []);
  const [notaryCommunes, setNotaryCommunes] = useState([]);
  const [rutValid] = useState("");
  const [expirationVisible, setExpirationVisible] = useState(false);
  const [instructionsVisible, setInstructionsVisible] = useState(false);
  const [loading, setLoading] = useState({ rut: false, power: false, company: false });
  const [companyFound, setCompanyFound] = useState(false);

  const dispatch = useDispatch();

  const dispatchPersonData = (data) => {
    dispatch(setEntity({ entity: role, data }));
    if (partyRole !== undefined && partyIndex !== undefined) {
      dispatch(setPartyAt({ role: partyRole, index: partyIndex, data }));
    }
  };

  useEffect(() => {
    setExpirationVisible(
      person?.expiration &&
        person.expiration.toString() !== "Invalid Date" &&
        person.expiration < moment()
    );
  }, [person?.expiration]);

  useEffect(() => {
    if (person?.region) {
      setCommunes(findCommunes(person.region) || []);
    }
  }, [person?.region]);

  useEffect(() => {
    if (person?.hasPower && person?.powerNotaryRegion) {
      setNotaryCommunes(findCommunes(person.powerNotaryRegion) || []);
    } else if (!person?.hasPower) {
      setNotaryCommunes([]);
    }
  }, [person?.hasPower, person?.powerNotaryRegion]);

  useEffect(() => {
    // Solo aplicar defaults si el campo no tiene valor (no sobreescribir datos existentes)
    dispatchPersonData({
      ...(!person?.commune    ? { commune: "Puerto Montt" } : {}),
      ...(!person?.region     ? { region: "Región de los Lagos" } : {}),
      ...(!person?.civilStatus ? { civilStatus: "no informado" } : {}),
      representsCompany: person?.representsCompany ?? false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const filteredNotaries = useMemo(() => {
    if (!person?.hasPower) return [];
    const regionFilter = normalize(person?.powerNotaryRegion || "");
    const communeFilter = normalize(person?.powerNotaryCommune || "");
    return notarias.filter((n) => {
      const nr = normalize(findRegion(n.commune) || "");
      const nc = normalize(n.commune);
      return (regionFilter ? nr === regionFilter : true) && (communeFilter ? nc === communeFilter : true);
    });
  }, [person?.hasPower, person?.powerNotaryRegion, person?.powerNotaryCommune]);

  const availableNotaries = filteredNotaries.length ? filteredNotaries : notarias;

  const formFields = useMemo(() => {
    const baseFields = parseStateToFields(person || {});
    return baseFields.map((field) => {
      if (field.name?.[0] === "powerNotaryName" && field.value && typeof field.value === "object") {
        return { ...field, value: field.value.displayName || field.value.name || "" };
      }
      if (["region", "commune", "powerNotaryRegion", "powerNotaryCommune"].includes(field.name?.[0])) {
        if (field.value && typeof field.value === "string") {
          return { ...field, value: safeText(field.value) };
        }
      }
      if (field.name?.[0] === "powerDate") {
        if (field.value && moment.isMoment(field.value) && field.value.isValid()) {
          return { ...field, value: field.value.format("YYYY-MM-DD") };
        }
        return { ...field, value: field.value && typeof field.value === "string" ? field.value : "" };
      }
      if (field.name?.[0] === "companyStatuteDate") {
        if (!field.value) return { ...field, value: null };
        const d = dayjs(field.value);
        return { ...field, value: d.isValid() ? d : null };
      }
      return field;
    });
  }, [person]);

  useEffect(() => {
    if (person?.powerNotaryName && typeof person.powerNotaryName === "object") {
      dispatchPersonData({
        powerNotaryName: person.powerNotaryName.displayName || person.powerNotaryName.name || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person?.powerNotaryName]);

  const handleRegionChange = (regionName) => {
    const selectedCommunes = findCommunes(regionName) || [];
    setCommunes(selectedCommunes);
    dispatchPersonData({ commune: selectedCommunes[0] || null, region: regionName });
  };

  const extractPowerData = async (rutValue = null) => {
    const personRut = rutValue || person?.powerPersonRut;
    if (!personRut) return;
    setLoading((prev) => ({ ...prev, power: true }));
    try {
      const response = await fetchRutificador(format(personRut));
      const { firstLastName, secondLastName, rut, name } = response.data;
      dispatchPersonData({
        powerPersonRut: rut || format(personRut),
        powerPersonName: name,
        powerPersonFirstLastName: firstLastName,
        powerPersonSecondLastName: secondLastName,
      });
    } catch (error) {
      if (error.response?.status === 404) toast("error", "RUT no está en la base de datos.");
      else handleError(error, toast, "Error al extraer datos del poder", `extractPowerData - ${role}`);
    }
    setLoading((prev) => ({ ...prev, power: false }));
  };

  const extractData = async (rutValue = null) => {
    const personRut = rutValue || person?.rut;
    setLoading((prev) => ({ ...prev, rut: true }));
    try {
      const response = await fetchRutificador(format(personRut));
      const { commune } = response.data;
      const regionName = findRegion(commune);
      setCommunes(regionName ? findCommunes(regionName) : []);
      dispatchPersonData({
        ...response.data,
        region: findRegion(commune) || "Región de los Lagos",
        commune: correctCommune(commune) || commune,
      });
    } catch (error) {
      if (error.response?.status === 404) toast("info", "Cliente no registrado, favor introducir datos manualmente.");
      else handleError(error, toast, "Error al obtener datos del RUT", `extractData - ${role}`);
    }
    setLoading((prev) => ({ ...prev, rut: false }));
  };

  const handleValidRut = (rutValue) => {
    if (!person?.name || !person?.firstLastName || !person?.secondLastName) {
      dispatchPersonData({ rut: format(rutValue) });
      extractData(rutValue);
    }
  };

  const handlePowerValidRut = (rutValue) => {
    dispatchPersonData({ powerPersonRut: format(rutValue) });
    extractPowerData(rutValue);
  };

  const handleCompanyRutValid = async (rutValue) => {
    dispatchPersonData({ companyRut: format(rutValue) });
    setLoading((prev) => ({ ...prev, company: true }));
    setCompanyFound(false);
    try {
      const res = await fetchRutificador(format(rutValue));
      const data = res.data;
      if (data && data.entityType === 'company') {
        dispatchPersonData({
          companyRut: data.companyRut || data.rut || format(rutValue),
          companyName: data.companyName || '',
          companyGiro: data.companyGiro || '',
          companyAddress: data.address || '',
        });
        setCompanyFound(true);
        toast('success', `Empresa encontrada: ${data.companyName || data.rut}`);
      }
    } catch {
      // No encontrada — el usuario llena manualmente, sin error
    }
    setLoading((prev) => ({ ...prev, company: false }));
  };

  const handleToggleCompany = () => {
    setCompanyFound(false);
    dispatchPersonData({ representsCompany: !person?.representsCompany });
  };

  const handleTogglePower = (checked) => {
    setNotaryCommunes(checked ? findCommunes("Región de los Lagos") || [] : []);
    dispatchPersonData(
      checked
        ? { hasPower: true, powerNotaryRegion: "Región de los Lagos", powerNotaryCommune: "Puerto Montt", powerDate: person?.powerDate || null }
        : { hasPower: false, powerDate: null, powerNotaryRegion: null, powerNotaryCommune: null, powerPersonRut: null, powerPersonName: null, powerPersonFirstLastName: null, powerPersonSecondLastName: null, powerNotaryName: null }
    );
  };

  return (
    <div
      className="person-form"
      style={{
        border: `1px solid ${accentColor}30`,
        borderTop: "none",
        borderRadius: "0 0 10px 10px",
        padding: "16px 16px 8px",
        background: "#fff",
      }}
    >
      {/* Loading modal */}
      <Modal
        open={!!loading.rut}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        maskStyle={{ backdropFilter: "blur(3px)" }}
        styles={{ content: { borderRadius: 12, padding: 0 } }}
      >
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 40, color: "#1677ff" }} spin />} />
          <div style={{ fontSize: 16, fontWeight: 600, marginTop: 12 }}>Buscando RUT</div>
          <div style={{ color: "rgba(0,0,0,0.45)" }}>Consultando registros y completando datos…</div>
        </div>
      </Modal>

      <ImageInstructionsModal visible={instructionsVisible} setVisible={setInstructionsVisible} />
      <ExpiredModal visible={expirationVisible} setVisible={setExpirationVisible} />

      <Form
        name={role}
        initialValues={{ sex: "M" }}
        onFieldsChange={(current) => {
          const fieldName = current?.[0]?.name?.[0];
          const fieldValue = current?.[0]?.value;
          if (!fieldName) return;

          if (fieldName === "hasPower") {
            handleTogglePower(Boolean(fieldValue));
            return;
          }
          if (fieldName === "representsCompany") {
            dispatchPersonData({ representsCompany: Boolean(fieldValue) });
            return;
          }
          if (fieldName === "powerNotaryRegion") {
            const communes = findCommunes(fieldValue) || [];
            setNotaryCommunes(communes);
            dispatchPersonData({ powerNotaryRegion: fieldValue, powerNotaryCommune: communes[0] || null, powerNotaryName: null });
            return;
          }
          if (fieldName === "powerNotaryCommune") {
            dispatchPersonData({ powerNotaryCommune: fieldValue, powerNotaryName: null });
            return;
          }
          if (fieldName === "powerNotaryName") {
            dispatchPersonData({ powerNotaryName: fieldValue });
            return;
          }
          if (fieldName === "powerDate") {
            dispatchPersonData({ powerDate: fieldValue || null });
            return;
          }
          dispatchPersonData(parseFieldsToState(current));
        }}
        fields={formFields}
        layout="vertical"
      >

        {/* ── IDENTIFICACIÓN ──────────────────────────────── */}
        <FieldSection icon={<IdcardOutlined />} label="Identificación" />
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="rut" label="RUT" validateStatus={rutValid} hasFeedback style={fi}>
              <RutInput
                autoFetch={!person?.name || !person?.firstLastName || !person?.secondLastName}
                onValidRut={handleValidRut}
                size="middle"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="docNum" label="N° doc." style={fi}>
              <Input size="middle" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="sex" label="Sexo" style={fi}>
              <Select size="middle">
                <Option value="F">F</Option>
                <Option value="M">M</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* ── DATOS PERSONALES ────────────────────────────── */}
        <FieldSection icon={<UserOutlined />} label="Datos personales" />
        <Form.Item
          name="name"
          label="Nombres"
          rules={[{ required: true, message: "Ingrese el nombre" }]}
          style={fi}
        >
          <Input size="middle" />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="firstLastName"
              label="Apellido Paterno"
              rules={[{ required: true, message: "Ingrese el apellido paterno" }]}
              style={fi}
            >
              <Input size="middle" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="secondLastName" label="Apellido Materno" style={fi}>
              <Input size="middle" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={14}>
            <Form.Item
              name="civilStatus"
              label="Estado civil"
              rules={[{ required: true, message: "Seleccione el estado civil" }]}
              style={fi}
            >
              <Select size="middle">
                <Option value="no informado">No informado</Option>
                <Option value="soltero">Soltero/a</Option>
                <Option value="casado">Casado/a</Option>
                <Option value="divorciado">Divorciado/a</Option>
                <Option value="viudo">Viudo/a</Option>
                <Option value="conviviente civil">Conviviente Civil</Option>
                <Option value="separado">Separado judicialmente</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              name="phone"
              label="Teléfono"
              rules={[{ required: true, message: "Ingrese el teléfono" }]}
              style={fi}
            >
              <Input size="middle" />
            </Form.Item>
          </Col>
        </Row>

        {/* ── UBICACIÓN ───────────────────────────────────── */}
        <FieldSection icon={<EnvironmentOutlined />} label="Contacto y ubicación" />
        <Form.Item
          name="address"
          label="Dirección"
          rules={[{ required: true, message: "Ingrese la dirección" }]}
          style={fi}
        >
          <Input size="middle" />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="region" label="Región" style={fi}>
              <Select onChange={handleRegionChange} size="middle">
                {regions.map((r) => {
                  const v = safeText(r.region);
                  return <Option key={v} value={v}>{v}</Option>;
                })}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="commune" label="Comuna" style={fi}>
              <Select size="middle">
                {communes.map((c) => {
                  const v = safeText(c);
                  return <Option key={v} value={v}>{v}</Option>;
                })}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Hidden form items for checkbox sync */}
        <Form.Item name="representsCompany" valuePropName="checked" style={{ display: "none" }}>
          <Checkbox />
        </Form.Item>
        <Form.Item name="hasPower" valuePropName="checked" style={{ display: "none" }}>
          <Checkbox />
        </Form.Item>

        {/* ── EMPRESA (toggle) ─────────────────────────────── */}
        <SectionToggle
          checked={!!person?.representsCompany}
          onToggle={handleToggleCompany}
          icon={<BankOutlined />}
          title="Actúa en representación de empresa"
          color="#7c3aed"
        />
        {person?.representsCompany && (
          <SectionBlock active color="#7c3aed">
            {/* RUT empresa — con auto-búsqueda */}
            <Row gutter={12} align="middle">
              <Col span={10}>
                <Form.Item
                  name="companyRut"
                  label={
                    <span>
                      RUT empresa{" "}
                      {loading.company && <LoadingOutlined style={{ color: "#7c3aed", marginLeft: 4 }} />}
                      {companyFound && !loading.company && (
                        <CheckCircleOutlined style={{ color: "#52c41a", marginLeft: 4 }} />
                      )}
                    </span>
                  }
                  style={fi}
                >
                  <RutInput
                    size="middle"
                    placeholder="76.543.210-K"
                    autoFetch={false}
                    onValidRut={handleCompanyRutValid}
                  />
                </Form.Item>
              </Col>
              <Col span={14}>
                <Form.Item name="companyName" label="Razón social" style={fi}>
                  <Input size="middle" placeholder="Empresa S.A." />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="companyGiro" label="Giro" style={fi}>
                  <Input size="middle" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="companyAddress" label="Dirección empresa" style={fi}>
                  <Input size="middle" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={14}>
                <Form.Item
                  name="companyStatuteDate"
                  label="Fecha certificado de Estatuto"
                  style={fi}
                  tooltip="Fecha del certificado de Estatuto actualizado del Registro de Empresas y Sociedades (aparece en cláusula SÉPTIMO)"
                >
                  <DatePicker
                    size="middle"
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    placeholder="Seleccionar fecha"
                    onChange={(date) =>
                      dispatchPersonData({ companyStatuteDate: date ? date.toISOString() : null })
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </SectionBlock>
        )}

        {/* ── PODER NOTARIAL (toggle) ──────────────────────── */}
        <SectionToggle
          checked={!!person?.hasPower}
          onToggle={() => handleTogglePower(!person?.hasPower)}
          icon={<SafetyCertificateOutlined />}
          title="Representado por poder notarial"
          color="#0369a1"
        />
        {person?.hasPower && (
          <SectionBlock active color="#0369a1">
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="powerPersonRut" label="RUT representante" validateStatus={rutValid} hasFeedback style={fi}>
                  <RutInput size="middle" autoFetch onValidRut={handlePowerValidRut} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="powerPersonName" label="Nombres" style={fi}>
                  <Input size="middle" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="powerPersonFirstLastName" label="Apellido Paterno" style={fi}>
                  <Input size="middle" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="powerPersonSecondLastName" label="Apellido Materno" style={fi}>
                  <Input size="middle" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12} style={{ marginBottom: 10 }}>
              <Col span={12}>
                <Button
                  disabled={!person?.powerPersonRut || !validate(person.powerPersonRut)}
                  type="primary"
                  block
                  size="small"
                  onClick={extractPowerData}
                  loading={loading.power}
                  style={{ background: "#0369a1", borderColor: "#0369a1" }}
                >
                  Extraer datos
                </Button>
              </Col>
              <Col span={12}>
                <UploadFile
                  type=".jpeg, .jpg, .png"
                  action="/cartransfers/folder"
                  entity={`${role}_power`}
                  label="Subir poder"
                  cartransferId={cartransferId}
                />
              </Col>
            </Row>

            <div
              style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
                color: "#0369a1", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #bae6fd",
              }}
            >
              Notaría del poder
            </div>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="powerNotaryRegion" label="Región" style={fi}>
                  <Select size="middle">
                    {regions.map((r) => {
                      const v = safeText(r.region);
                      return <Option key={v} value={v}>{v}</Option>;
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="powerNotaryCommune" label="Comuna" style={fi}>
                  <Select size="middle" placeholder="Seleccione una comuna">
                    {(notaryCommunes || []).map((c) => {
                      const v = safeText(c);
                      return <Option key={v} value={v}>{v}</Option>;
                    })}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="powerNotaryName" label="Notaría" style={fi}>
              <Select size="middle" showSearch optionFilterProp="children" placeholder="Seleccione una notaría">
                {availableNotaries.map((n) => {
                  const v = safeText(n.displayName || `${n.num} ${n.name}`);
                  return <Option key={v} value={v}>{v}</Option>;
                })}
              </Select>
            </Form.Item>
            <Form.Item
              name="powerDate"
              label="Fecha del poder"
              help="Formato: Año-Mes-Día (ej: 2025-11-08)"
              style={fi}
            >
              <Input size="middle" placeholder="2025-11-08" maxLength={10} />
            </Form.Item>
          </SectionBlock>
        )}
      </Form>
    </div>
  );
};

export default PersonForm;
